import React, { useEffect, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { Collection } from "winslow-core";
import {
  apiGetUser,
  apiUpdateWorkspace,
  apiLeaveWorkspace,
  apiGetWorkspaceMembers,
  apiUpdateWorkspaceSelfService,
  apiUpdateWorkspaceSlackSelfService,
  apiResetConversationHistory,
} from "../../api";
import { toastFail, toastSuccess } from "../../config";
import { useSelector, useDispatch } from "react-redux";
import {
  setUser,
  selectUser,
  selectCurrentCollectionId,
  selectEditingCollection,
  setEditingCollection,
} from "../../redux/features/appData/appDataSlice";
import { useNavigate, useLocation } from "react-router-dom";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import { postImage } from "../../services/client";
import MemberManager from "./MemberManager";
import "./Workspaces.css";
import { useAuth } from "../../hooks/useAuth";
import { apiRevokeSlack } from "../../services/OAuthService";
import Modal from "../shared/Modal";
import ModalDeleteWorkspace from "./ModalDeleteWorkspace";
import { ModalSlackProcessing } from "./ModalSlackProcessing";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import WorkspaceConnectedApps from "./WorkspaceConnectedApps";
import { UserRoundIcon } from "lucide-react";

import { apiUpdateCollectionCompanyInfo } from "../../services/CollectionService";

interface Workspace {
  id: string;
  name: string;
  image?: string;
  ownerId: string;
  slackSelfService: boolean;
  slackSourceUserId?: string;
  companyName?: string;
  companyDescription?: string;
}

interface User {
  id: string;
  workspaces: Workspace[];
  currentWorkspaceId: string;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
}

interface SlackUserOption {
  label: string;
  value: string;
}

const TABS_NUMBERS = {
  BASICS: "1",
  CONNECT: "2",
  TEAM: "3",
  MUTED: "4",
};

export default function Workspaces() {
  const auth = useAuth();
  const storeUser = useSelector(selectUser) as User | null;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { reloadAll } = useCoreDataLoader();

  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(
    null
  );
  const [changed, setChanged] = useState<boolean>(false);
  const [companyNameChanged, setCompanyNameChanged] = useState<boolean>(false);
  const [companyDescriptionChanged, setCompanyDescriptionChanged] =
    useState<boolean>(false);

  const [logoFile, setLogoFile] = useState<string>("");
  const logoFileRef = useRef<HTMLInputElement>(null);

  const [leaveDialogOpen, setLeaveDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  const [tabNumber, setTabNumber] = useState<string>(TABS_NUMBERS.BASICS);

  const [slackUserOptions, setSlackUserOptions] = useState<
    SlackUserOption[] | null
  >(null);
  const [slackSelfService, setSlackSelfService] = useState<boolean>(false);

  const [connectToSlackModalIsOpen, setConnectToSlackModalIsOpen] =
    useState<boolean>(false);
  const currentCollection = useSelector(
    selectEditingCollection
  ) as Collection | null;

  useEffect(() => {
    auth.checkStatus();

    // if we have a tab in query string use it
    const queryParams = new URLSearchParams(location.search);
    if (queryParams) {
      const tab = queryParams.get("tab");
      if (tab) {
        console.log("[Workspace] setting tab: ", tab);
        setTabNumber(tab);
      }
    }
  }, []);

  useEffect(() => {
    if (!storeUser) {
      return;
    }

    // if not all that stuff, then just grab the right workspace
    let index = storeUser.workspaces.findIndex(
      (workspace) => workspace.id === storeUser.currentWorkspaceId
    );

    if (index === -1) {
      console.error(
        "[workspaces/useEffect] can't get current workspace, defaulting to 0th"
      );
      index = 0;
    }

    setCurrentWorkspace({ ...storeUser.workspaces[index] });
    setSlackSelfService(storeUser.workspaces[index].slackSelfService);
  }, [storeUser]);

  useEffect(() => {
    (async () => {
      if (storeUser?.currentWorkspaceId) {
        const finalMembers = await apiGetWorkspaceMembers(
          storeUser.currentWorkspaceId
        );

        if (finalMembers) {
          finalMembers.sort((a: Member, b: Member) => {
            const aText = a.firstName ?? a.lastName ?? "";
            const bText = b.firstName ?? b.lastName ?? "";
            return aText.toLowerCase().localeCompare(bText.toLowerCase());
          });

          const options = finalMembers.map((member: Member) => {
            return {
              label: member.firstName + " " + member.lastName,
              value: member.id,
            };
          });

          options.unshift({
            label: "(none configured)",
            value: "",
          });

          setSlackUserOptions(options);
        }
      }
    })();
  }, [storeUser]);

  const leaveWorkplace = async () => {
    if (!currentWorkspace || !storeUser) return;

    const result = await apiLeaveWorkspace(currentWorkspace.id, storeUser.id);
    if (result) {
      dispatch(setUser(result));
      toast("Removed from workspace", toastSuccess);
      navigate("/home");
      return;
    }
  };

  const handleWorkspaceName = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentWorkspace) return;

    setCurrentWorkspace({ ...currentWorkspace, name: e.target.value });
    setChanged(true);
  };

  const updateWorkspaceName = async () => {
    if (!currentWorkspace || !storeUser) return;

    await apiUpdateWorkspace(currentWorkspace);
    const updatedUser = await apiGetUser(storeUser.id);
    dispatch(setUser(updatedUser));
    toast("Workspace updated.", toastSuccess);
  };

  const handleCompanyName = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentCollection) return;

    dispatch(
      setEditingCollection({
        ...currentCollection,
        companyName: e.target.value,
      })
    );
    setCompanyNameChanged(true);
  };

  const updateCompanyName = async () => {
    if (!currentCollection || !storeUser) return;

    try {
      await apiUpdateCollectionCompanyInfo(
        currentCollection.id,
        currentCollection.companyName || null,
        currentCollection.companyPrompt || null
      );
      setCompanyNameChanged(false);
      toast("Company info updated.", toastSuccess);
    } catch (error) {
      toast("Failed to update company info.", toastFail);
    }
  };

  const handleCompanyPrompt = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!currentCollection) return;

    dispatch(
      setEditingCollection({
        ...currentCollection,
        companyPrompt: e.target.value,
      })
    );

    setCompanyDescriptionChanged(true);
  };

  const updateCompanyDescription = async () => {
    if (!currentCollection || !storeUser) return;

    // Assuming there's an API to update collection
    // Replace with the actual API call to update collection
    try {
      await apiUpdateCollectionCompanyInfo(
        currentCollection.id,
        currentCollection.companyName || null,
        currentCollection.companyPrompt || null
      );
      setCompanyDescriptionChanged(false);
      toast("Company prompt updated.", toastSuccess);
    } catch (error) {
      toast("Failed to update company prompt.", toastFail);
    }
  };

  const resetConversationHistory = async () => {
    if (!currentCollection || !currentCollection.id) {
      toast("No collection selected", toastFail);
      return;
    }

    if (!storeUser) return;

    await apiResetConversationHistory(storeUser.id, currentCollection.id);
    toast("Conversation history reset.", toastSuccess);
  };

  const changeTab = (_e: React.SyntheticEvent, newValue: string) => {
    setTabNumber(newValue);
  };

  const changeLogo = () => {
    if (logoFileRef?.current) {
      const inputButton = logoFileRef.current;
      setLogoFile("");
      inputButton.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!storeUser) {
      return;
    }

    if (event.target.files && event.target.files?.length < 1) {
      return;
    }

    if (event.target.files && event.target.files[0].size > 10485760) {
      toast("Logo is too big! Please keep it under 10MB", toastFail);
      return;
    }

    const theFile = event.target.files ? event.target.files[0] : null;

    if (!theFile) {
      toast("Did not get a valid file", toastFail);
      return;
    }

    (async () => {
      toast("Uploading...", toastSuccess);

      try {
        const response = await postImage(storeUser.id, theFile, () => {});

        if (response && response.status === 200) {
          const theWorkspace = {
            ...currentWorkspace,
            image: response.data.data,
          };
          setCurrentWorkspace(theWorkspace as Workspace);
          await apiUpdateWorkspace(theWorkspace);

          // update the sidebar
          const apiUser = await apiGetUser(storeUser.id);

          if (!apiUser) {
            console.error(
              "[Workspaces/handleFileChange] error refreshing user!"
            );
            return;
          }

          dispatch(setUser(apiUser));
        } else {
          toast("File not uploaded", toastFail);
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        toast("Error uploading file, please try again.", toastFail);
      }
    })();
  };

  const finalizeSlackProcessing = () => {
    setConnectToSlackModalIsOpen(false);
    if (storeUser?.currentWorkspaceId) {
      reloadAll(storeUser.currentWorkspaceId);
    }
  };

  // find default slack user
  let defaultOption = 0;

  if (
    slackUserOptions &&
    storeUser?.workspaces &&
    storeUser.currentWorkspaceId
  ) {
    const index = storeUser.workspaces.findIndex((workspace) => {
      return workspace.id === storeUser.currentWorkspaceId;
    });

    if (index !== -1) {
      const matchIndex = slackUserOptions.findIndex((option) => {
        return option.value === storeUser.workspaces[index].slackSourceUserId;
      });

      if (matchIndex !== -1) {
        defaultOption = matchIndex;
      }
    }
  }

  const handleSlackSelfServiceChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!currentWorkspace) return;

    setSlackSelfService(e.target.checked);
    const updateSelfService = async () => {
      const result = await apiUpdateWorkspaceSlackSelfService(
        currentWorkspace.id,
        e.target.checked
      );

      if (result) {
        setCurrentWorkspace({
          ...currentWorkspace,
          slackSelfService: e.target.checked,
        });

        toast("Slack Self service updated.", toastSuccess);
      } else {
        toast("Slack Self service update failed.", toastFail);
      }
    };

    updateSelfService();
  };

  return (
    <>
      {currentWorkspace && (
        <div id="workspaces">
          <div className="settings-title">
            <h1>Workspace Settings</h1>
          </div>

          <div className="workspaces-body">
            <Box sx={{ width: "100%", typography: "body1" }}>
              <TabContext value={tabNumber}>
                <Box sx={{}}>
                  <TabList
                    onChange={changeTab}
                    aria-label="lab API tabs example"
                    sx={{
                      "&.MuiTabPanel": { padding: "0px", scrollbarWidth: 0 },
                    }}
                    TabIndicatorProps={{
                      style: { background: "var(--text-color-primary)" },
                    }}>
                    <Tab
                      sx={{
                        "&.Mui-selected": {
                          color: "var(--text-color-primary)",
                        },
                        color: "var(--text-color-secondary)",
                      }}
                      label="Basic Details"
                      value="1"
                    />

                    <Tab
                      sx={{
                        "&.Mui-selected": {
                          color: "var(--text-color-primary)",
                        },
                        color: "var(--text-color-secondary)",
                      }}
                      label="Members"
                      value="3"
                    />

                    <Tab
                      sx={{
                        "&.Mui-selected": {
                          color: "var(--text-color-primary)",
                        },
                        color: "var(--text-color-secondary)",
                      }}
                      label="Connected Apps"
                      value="4"
                    />
                  </TabList>
                </Box>

                <TabPanel value="1">
                  <div className="workspaces-container">
                    <div className="settings-section">
                      <div className="settings-row">
                        <div className="settings-header">
                          <div className="row">
                            <h2>Workspace Name</h2>
                          </div>
                          <span
                            style={{ color: "var(--text-color-secondary)" }}>
                            Update the name of your workspace that appears on
                            the sidebar.
                          </span>
                        </div>
                        <div className="workspaces-name">
                          <input
                            type="text"
                            value={currentWorkspace.name}
                            onChange={handleWorkspaceName}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                updateWorkspaceName();
                              }
                            }}
                            aria-label="Workspace name"
                          />
                          <button
                            id="save-button"
                            type="button"
                            onClick={updateWorkspaceName}
                            disabled={!changed}>
                            Save
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="settings-section">
                      <div className="settings-row">
                        <div className="settings-header">
                          <div className="row">
                            <h2>Company Name</h2>
                          </div>
                          <span
                            style={{ color: "var(--text-color-secondary)" }}>
                            Update your company name.
                          </span>
                        </div>
                        <div className="workspaces-name">
                          <input
                            type="text"
                            value={currentCollection?.companyName || ""}
                            onChange={handleCompanyName}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                updateCompanyName();
                              }
                            }}
                            aria-label="Company name"
                          />
                          <button
                            id="save-button"
                            type="button"
                            onClick={updateCompanyName}
                            disabled={!companyNameChanged}>
                            Save
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="settings-section">
                      <div className="settings-row">
                        <div className="settings-header">
                          <div className="row">
                            <h2>Company Prompt</h2>
                          </div>
                          <span
                            style={{ color: "var(--text-color-secondary)" }}>
                            Update your company prompt.
                          </span>
                        </div>
                        <div className="workspaces-name">
                          <textarea
                            value={currentCollection?.companyPrompt || ""}
                            onChange={handleCompanyPrompt}
                            rows={4}
                            style={{
                              width: "100%",
                              resize: "vertical",
                              borderRadius: "4px",
                              padding: "8px",
                            }}
                            aria-label="Company prompt"
                          />
                          <button
                            id="save-button"
                            type="button"
                            onClick={updateCompanyDescription}
                            disabled={!companyDescriptionChanged}>
                            Save
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="settings-section">
                      <div className="settings-row">
                        <div className="settings-header">
                          <div className="row">
                            <h2>Reset Conversation History</h2>
                          </div>
                          <span
                            style={{ color: "var(--text-color-secondary)" }}>
                            Reset the conversation history for your workspace.
                          </span>
                        </div>
                        <div className="settings-action">
                          <button
                            className="save-button-enabled"
                            onClick={resetConversationHistory}>
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>

                    {storeUser?.id === currentWorkspace?.ownerId && (
                      <div className="settings-section">
                        <div className="settings-row">
                          <div className="settings-header">
                            <div className="row">
                              <h2>Delete Workspace</h2>
                            </div>
                            <span
                              style={{ color: "var(--text-color-secondary)" }}>
                              Delete your workspace and all contents used for
                              answering questions.
                            </span>
                          </div>
                          <div className="settings-action">
                            <button
                              className="save-button-disabled"
                              onClick={() => setDeleteDialogOpen(true)}>
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </TabPanel>

                <TabPanel value="3">
                  <MemberManager />
                </TabPanel>
                <TabPanel value="4">
                  <WorkspaceConnectedApps
                    slackSelfService={slackSelfService}
                    handleSlackSelfServiceChange={handleSlackSelfServiceChange}
                  />
                </TabPanel>
              </TabContext>
            </Box>
          </div>

          <Modal
            isOpen={leaveDialogOpen}
            style={null}
            onClose={() => {
              setLeaveDialogOpen(false);
            }}>
            <div className="shares-card-delete-container">
              <div className="shares-card-delete-title">
                <h2>Delete?</h2>
                <span style={{ color: "var(--text-color-secondary)" }}>
                  Are you sure you want to leave this workspace?
                </span>
              </div>
              <div className="shares-card-delete-buttons">
                <button
                  className="shares-card-delete-buttons-cancel"
                  onClick={() => {
                    setLeaveDialogOpen(false);
                  }}>
                  Cancel
                </button>
                <button
                  onClick={leaveWorkplace}
                  className="shares-card-delete-buttons-delete">
                  Yes, Delete
                </button>
              </div>
            </div>
          </Modal>

          <ModalSlackProcessing
            isOpen={connectToSlackModalIsOpen}
            onClose={finalizeSlackProcessing}
          />

          {deleteDialogOpen && (
            <ModalDeleteWorkspace
              isOpen={deleteDialogOpen}
              onClose={() => {
                setDeleteDialogOpen(false);
              }}
              workspaceId={storeUser?.currentWorkspaceId || ""}
            />
          )}
        </div>
      )}
    </>
  );
}
